import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import utils, { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    usePopup,
    TFormSchema,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_LCNE_USR_INFO } from "@/tra/tancis/ptli/usr/lcneusraprv/services/PtliLcneUsrAprvService";
import { LCNE_USE_TP_CD_UTIL } from "@/tra/tancis/ptli/usr/lcneusr/services/PtliLcneUsrService";

/*
 * License User Details
 * !== 면허 사용자 상세 ==!
 */
export const PTLI0907002Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0025", "auth_eswi0004", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { lcneCoAplyId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { getResource } = useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "LCM0001" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // License User Information !== 면허 사용자 정보 ==!
        lcneUsrInfo: useForm({
            defaultSchema: SF_LCNE_USR_INFO,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 면허 사용자 정보 조회 ==!
        getLcneUsrInfo: useFetch({
            api: () => APIS.getLcneUsrInfo(lcneCoAplyId),
            enabled: !!lcneCoAplyId,
            onSuccess: (data) => {
                const lcneUsr = data.lcneUsr.content;

                const lcneTpComnCdList = getResource("comnCd", "LCM0001");
                const lcneTpCdArr = LCNE_USE_TP_CD_UTIL.initLcneUseTpCdSchema({
                    lcneUseTpCdArray: lcneUsr.lcneTpCdArray,
                    form: form.lcneUsrInfo,
                    lcneTpComnCdList: lcneTpComnCdList,
                    t: t,
                });
                // lcneUsr.lcneTpCd = lcneTpCdArr;

                form.lcneUsrInfo.setValues(lcneUsr);
                form.lcneUsrInfo.setEditable("rtrnRsn", true);

                if (lcneUsr.aprvCoYn === "N") {
                    form.lcneUsrInfo.setSchema("rtrnRsn", { readOnly: true, disabled: true, required: false });
                }
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Execute License User Approval !== 면허 사용자 승인 ==!
        executeLcneUsrAprv: useFetch({
            api: (data) => APIS.executeLcneUsrAprv({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00066", // Successfully approved., !== 정상적으로 승인되었습니다. ==!
                });
                fetch.getLcneUsrInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.lcneUsrInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // Execute License User Return !== 면허 사용자 반려 ==!
        executeLcneUsrRtrn: useFetch({
            api: (data) => APIS.executeLcneUsrRtrn({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00065", // Successfully returned., !== 정상적으로 반려되었습니다. ==!
                });
                fetch.getLcneUsrInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.lcneUsrInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Execute License User Approval !== 면허 사용자 승인 ==!
        executeLcneUsrAprv: () => {
            form.lcneUsrInfo.setSchema("rtrnRsn", { required: false });
            form.lcneUsrInfo.handleSubmit(
                () => {
                    const data = form.lcneUsrInfo.getValues();
                    data.lcneTpCdArray = LCNE_USE_TP_CD_UTIL.makeLcneUseTpCdDtoArray(form.lcneUsrInfo, "BASE");
                    modal.openModal({
                        content: "msg.com.00053", // Confirm approve? !== 승인하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.executeLcneUsrAprv.fetch(data);
                        },
                    });
                },
                () => {},
            )();
        },
        // Execute License User Return !== 면허 사용자 반려 ==!
        executeLcneUsrRtrn: () => {
            if (!comnUtils.isEmpty(form.lcneUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.lcneUsrInfo.handleSubmit(
                () => {
                    const formData = form.lcneUsrInfo.getValues();
                    formData.aprvCoYn = aprvCoYn;

                    if (aprvCoYn == "Y") {
                        const handlePopup = (data: any) => {
                            formData.rtrnRsn = data;

                            modal.closeModal();

                            fetch.executeLcneUsrRtrn.fetch(formData);
                        };

                        modal.openModal({
                            content: <ModalRtrnRsn handlePopup={handlePopup} />,
                            size: "sm",
                            draggable: true,
                        });
                    } else {
                        (formData.rtrnRsn = "Reject"),
                            modal.openModal({
                                content: "msg.com.00052", // Confirm return? !== 반려하시겠습니까? ==!
                                onConfirm: () => {
                                    fetch.executeLcneUsrRtrn.fetch(formData);
                                },
                            });
                    }
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    const aprvId = form.lcneUsrInfo.watch("aprvId");
    const rtrnYn = form.lcneUsrInfo.watch("rtrnYn");
    const ectYn = form.lcneUsrInfo.watch("ectYn");
    const aprvCoYn = form.lcneUsrInfo.watch("aprvCoYn");

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getLcneUsrInfo.fetch();
        form.lcneUsrInfo.setEditable(false);
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.coTin}></Group.Control>
                                <Group.Control {...form.lcneUsrInfo.schema.coNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.lcneUsrInfo.schema.lcneCoAplcNm}
                                    label={ectYn === "Y" ? t("L_CO_APLC_NM") : t("L_LCNE_CO_APLC_NM")}
                                ></Group.Control>
                                <Group.Control {...form.lcneUsrInfo.schema.cstmOfceCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.rprsTelno}></Group.Control>
                                <Group.Control {...form.lcneUsrInfo.schema.rprsFaxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.rprsEml}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.coAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.ectYn}></Group.Control>
                            </Group.Row>
                            {ectYn === "N" && (
                                <Group.Row>
                                    <Group.Control {...form.lcneUsrInfo.schema.lcneTpCd}></Group.Control>
                                </Group.Row>
                            )}
                            {ectYn === "N" &&
                                LCNE_USE_TP_CD_UTIL.isLcneUseTpCdTarget({
                                    lcneTpCd: "1",
                                    lcneTpCdChekedArr: form.lcneUsrInfo.getValue("lcneTpCd"),
                                }) && (
                                    <Group.Row>
                                        <Group.Control
                                            {...form.lcneUsrInfo.schema[LCNE_USE_TP_CD_UTIL.getLcneUseTpCdVarNm("1")]}
                                        ></Group.Control>
                                    </Group.Row>
                                )}
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.mdfyRsn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.bsopPrcssStat}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0907001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right hidden={!!aprvId || rtrnYn === "Y"}>
                    <Layout.Button
                        color="blue"
                        onClick={() => {
                            handler.executeLcneUsrAprv();
                        }}
                    >
                        {t("B_APV")}
                    </Layout.Button>
                    <Layout.Button
                        color="error"
                        onClick={() => {
                            handler.executeLcneUsrRtrn();
                        }}
                    >
                        {aprvCoYn === "Y" ? t("B_RTRN") : t("B_RJCT")}
                    </Layout.Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};

/*
 * !== 반려사유 Modal ==!
 */
export const ModalRtrnRsn = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "COM0024" }],
    });

    const { data, handlePopup } = props;

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            rtrnRsn: { type: "textarea", label: "L_RTRN_RSN", required: true, controlSize: 10, maxLength: 2000 },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    const handler = {
        submitBtnClick: () => {
            handlePopup(form.getValue("rtrnRsn"));
        },
    };

    return (
        <>
            <Page>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.rtrnRsn} />
                            </Group.Row>
                        </Group.Section>
                        <Group.Section>
                            <Layout>
                                <Layout.Right>
                                    <Button
                                        role={"submit"}
                                        onClick={() => {
                                            handler.submitBtnClick();
                                        }}
                                    />
                                </Layout.Right>
                            </Layout>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
